import {
    buildSearchMatchPreviewExcerpt,
    buildSearchMatchSnippet,
    hasSearchHighlightMatch
} from './searchHighlightCore';

export const URL_META_DESCRIPTION_VISIBLE_WINDOW = 120;
export const URL_META_DESCRIPTION_EXCERPT_LENGTH = 160;
export const HIDDEN_FIELD_MATCH_OFFSET = 32;

export const SEARCH_SNIPPET_FIELD_PRIORITY = [
    { key: 'caption', label: 'Caption' },
    { key: 'keywords', label: 'Keywords' },
    { key: 'ocrText', label: 'OCR' },
    { key: 'documentText', label: 'Document' },
    { key: 'urlMetaTitle', label: 'Link' },
    { key: 'urlMetaDescription', label: 'Link' },
    { key: 'fileDisplayName', label: 'File' },
    { key: 'imageDisplayName', label: 'File' },
    { key: 'urlMetaUrl', label: 'Link' }
];

export const normalizeSnippetValue = value => {
    if (Array.isArray(value)) {
        return value.filter(Boolean).join(', ');
    }

    return String(value || '').trim();
};

export const getMatchingUrlMetaWithField = (message, fieldName, searchTerm) => {
    if (!Array.isArray(message?.urlMeta)) {
        return null;
    }

    return message.urlMeta.find(meta => {
        const value = normalizeSnippetValue(meta?.[fieldName]);
        return value.length > 0 && hasSearchHighlightMatch(value, searchTerm);
    }) || null;
};

export const buildSearchSnippetFields = (message, searchTerm) => {
    const urlMetaWithTitle = getMatchingUrlMetaWithField(message, 'title', searchTerm);
    const urlMetaWithDescription = getMatchingUrlMetaWithField(message, 'description', searchTerm);
    const urlMetaWithUrl = getMatchingUrlMetaWithField(message, 'url', searchTerm);

    return {
        caption: normalizeSnippetValue(message?.caption),
        keywords: normalizeSnippetValue(message?.keywords),
        ocrText: normalizeSnippetValue(message?.ocrText),
        documentText: normalizeSnippetValue(message?.documentText),
        urlMetaTitle: normalizeSnippetValue(urlMetaWithTitle?.title),
        urlMetaDescription: normalizeSnippetValue(urlMetaWithDescription?.description),
        fileDisplayName: normalizeSnippetValue(message?.file?.displayName || message?.file?.name),
        imageDisplayName: normalizeSnippetValue(message?.image?.displayName || message?.image?.name),
        urlMetaUrl: normalizeSnippetValue(urlMetaWithUrl?.url)
    };
};

export const selectHiddenFieldSearchMatch = (
    message,
    searchTerm,
    hiddenFields,
    snippetLength = 120,
    options = {}
) => {
    const term = String(searchTerm || '').trim();
    if (term.length < 2) {
        return null;
    }

    const fields = buildSearchSnippetFields(message, term);
    const excludedNormalizedValues = options?.excludedNormalizedValues instanceof Set
        ? options.excludedNormalizedValues
        : null;
    const match = SEARCH_SNIPPET_FIELD_PRIORITY.find(field => {
        if (!hiddenFields?.[field.key]) {
            return false;
        }

        if (excludedNormalizedValues?.has(fields[field.key].toLowerCase())) {
            return false;
        }

        return hasSearchHighlightMatch(fields[field.key], term);
    });

    if (!match) {
        return null;
    }

    const snippet = buildSearchMatchSnippet(fields[match.key], term, snippetLength, {
        preferredMatchOffset: HIDDEN_FIELD_MATCH_OFFSET
    });
    if (!snippet) {
        return null;
    }

    return {
        key: match.key,
        label: match.label,
        snippet
    };
};

export const buildUrlMetaDescriptionSearchMatchExcerpt = (
    description,
    searchTerm,
    {
        isCollapsed = true,
        visibleWindow = URL_META_DESCRIPTION_VISIBLE_WINDOW,
        maxLength = URL_META_DESCRIPTION_EXCERPT_LENGTH
    } = {}
) => {
    if (!isCollapsed) {
        return null;
    }

    return buildSearchMatchPreviewExcerpt(description, searchTerm, {
        maxLength,
        visibleWindow
    });
};
